import { useEffect, useRef, useState, useSyncExternalStore, type FormEvent, type ReactNode } from 'react';
import { Icon } from '../../design/Icon';
import { Alert } from '../../design/ui/Alert';
import { PasswordField, StrengthMeter, TextField, passwordScore } from '../../design/ui/Fields';
import { Logo } from '../../design/ui/Logo';
import type { AuthService, AuthState } from './AuthService';
import './auth.css';

export const MIN_PASSWORD_LENGTH = 12;

type Mode = 'login' | 'register';
type ServerError = AuthState['error'];

/**
 * Lien d'une fonctionnalité pas encore disponible : affiché comme dans la maquette Auth, sans action.
 * Voir `Docs/coding/todo.md`.
 */
function SoonLink({ children }: { children: ReactNode }) {
  return (
    <a className="lh-link" href="#" aria-disabled="true" title="Bientôt disponible" onClick={(event) => event.preventDefault()}>
      {children}
    </a>
  );
}

/** Titre de l'écran : reçoit le focus à l'arrivée sur la vue (changement connexion ↔ création). */
function Head({ title, subtitle, logo, focus }: { title: string; subtitle?: string; logo: number; focus: boolean }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focus) ref.current?.focus();
  }, [focus]);
  return (
    <div className="auth-head">
      <Logo size={logo} />
      <h1 ref={ref} className="auth-title" tabIndex={-1}>{title}</h1>
      {subtitle ? <p className="auth-sub">{subtitle}</p> : null}
    </div>
  );
}

interface ViewProps {
  auth: AuthService;
  busy: boolean;
  email: string;
  setEmail: (value: string) => void;
  /** Erreur du serveur à afficher (déjà filtrée : plus celle d'une autre vue). */
  serverError: ServerError;
  dismissError: () => void;
  onSwitch: () => void;
  /** Vrai après un changement de vue : le titre prend le focus. */
  focusTitle: boolean;
}

/** Connexion (maquette 1a) : e-mail et mot de passe maître sur la même page. */
function LoginView({ auth, busy, email, setEmail, serverError, dismissError, onSwitch, focusTitle }: ViewProps) {
  const [password, setPassword] = useState('');
  const locked = (serverError?.code === 'RATE_LIMITED' || serverError?.code === 'TOO_MANY_ATTEMPTS') && serverError.retryAfterSeconds !== undefined;
  const wrongCredentials = serverError?.code === 'INVALID_CREDENTIALS' || serverError?.code === 'CRYPTO';
  const otherError = serverError && !locked && !wrongCredentials ? serverError.message : undefined;
  const disabled = busy || locked;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    await auth.login(email, password);
  };

  return (
    <form className="auth-card" onSubmit={(e) => void submit(e)} aria-busy={busy}>
      <Head focus={focusTitle} logo={56} title="Mon espace LifeHub" subtitle="Vos coffres, vos services, vos réglages." />
      {locked ? (
        <Alert key={serverError.message} tone="danger" title="Accès temporairement bloqué" countdown={serverError.retryAfterSeconds} onEnd={dismissError}>
          Trop de tentatives. Réessayez à la fin du décompte.
        </Alert>
      ) : null}
      {otherError ? <Alert tone="danger">{otherError}</Alert> : null}
      <TextField label="E-mail" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} invalid={wrongCredentials} disabled={disabled} />
      <PasswordField
        label="Mot de passe maître"
        autoComplete="current-password"
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={wrongCredentials ? serverError?.message : undefined}
        disabled={disabled}
      />
      <div className="auth-row">
        <label className="lh-check lh-check-box auth-soon" title="Bientôt disponible">
          <input type="checkbox" disabled />
          <span className="lh-box" aria-hidden="true" />
          Se souvenir de moi
        </label>
        <SoonLink>Mot de passe oublié ?</SoonLink>
      </div>
      <button type="submit" className={'lh-btn lh-btn-primary lh-btn-block' + (busy ? ' lh-btn-loading' : '')} disabled={disabled} aria-busy={busy || undefined}>
        {busy ? <span className="lh-spin" aria-hidden="true" /> : null}
        {busy ? 'Connexion…' : 'Se connecter'}
      </button>
      <div className="lh-div" role="separator">ou</div>
      <button type="button" className="lh-btn lh-btn-secondary lh-btn-block" disabled title="Bientôt disponible">
        <Icon name="fingerprint" size={20} />
        Clé d’accès / biométrie
      </button>
      <p className="auth-foot">
        <a className="lh-link" href="#" onClick={(event) => { event.preventDefault(); onSwitch(); }}>Créer un compte</a>
        <span aria-hidden="true">·</span>
        <SoonLink>Rejoindre un hub familial</SoonLink>
      </p>
    </form>
  );
}

/** Création de compte (maquette 1f) : la clé de récupération s'affiche ensuite (`RecoveryKeyDialog`). */
function RegisterView({ auth, busy, email, setEmail, serverError, onSwitch, focusTitle }: ViewProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [localError, setLocalError] = useState<{ field: 'password' | 'confirm'; message: string } | null>(null);
  const emailTaken = serverError?.code === 'EMAIL_ALREADY_REGISTERED';
  const otherError = serverError && !emailTaken ? serverError.message : undefined;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setLocalError(null);
    if (password.length < MIN_PASSWORD_LENGTH) return setLocalError({ field: 'password', message: `Le mot de passe maître comporte au moins ${MIN_PASSWORD_LENGTH} caractères.` });
    if (password !== confirm) return setLocalError({ field: 'confirm', message: 'Les deux mots de passe ne correspondent pas.' });
    await auth.register(email, password);
  };

  return (
    <form className="auth-card auth-card--register" onSubmit={(e) => void submit(e)} aria-busy={busy}>
      <Head focus={focusTitle} logo={48} title="Créer mon espace LifeHub" />
      {otherError ? <Alert tone="danger">{otherError}</Alert> : null}
      <TextField label="E-mail" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} error={emailTaken ? serverError?.message : undefined} disabled={busy} />
      <div className="auth-stack">
        <PasswordField
          label="Mot de passe maître"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          help={`Au moins ${MIN_PASSWORD_LENGTH} caractères.`}
          error={localError?.field === 'password' ? localError.message : undefined}
          disabled={busy}
        />
        <StrengthMeter score={passwordScore(password)} />
      </div>
      <PasswordField
        label="Confirmer le mot de passe"
        autoComplete="new-password"
        required
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        error={localError?.field === 'confirm' ? localError.message : undefined}
        disabled={busy}
      />
      <Alert tone="warning">
        Ce mot de passe ne peut pas être récupéré par LifeHub s’il est perdu. Une clé de récupération vous sera montrée une seule fois à l’étape suivante.
      </Alert>
      <label className="lh-check lh-check-box">
        <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} disabled={busy} />
        <span className="lh-box" aria-hidden="true">{understood ? <Icon name="check" size={14} /> : null}</span>
        J’ai compris
      </label>
      <button type="submit" className={'lh-btn lh-btn-primary lh-btn-block' + (busy ? ' lh-btn-loading' : '')} disabled={busy || !understood} aria-busy={busy || undefined}>
        {busy ? <span className="lh-spin" aria-hidden="true" /> : null}
        {busy ? 'Création du compte…' : 'Créer mon compte'}
      </button>
      <p className="auth-foot">
        Déjà un compte ?
        <a className="lh-link" href="#" onClick={(event) => { event.preventDefault(); onSwitch(); }}>Se connecter</a>
      </p>
    </form>
  );
}

/** Connexion et création de compte : deux écrans distincts. Le mot de passe maître sert à dériver les clés ici, sur l'appareil. */
export function AuthScreen({ auth }: { auth: AuthService }) {
  const state = useSyncExternalStore(auth.subscribe, auth.getState);
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [switched, setSwitched] = useState(false);
  // Une erreur déjà vue (ou arrivée à terme, comme un blocage) n'est plus affichée, et ne suit pas dans l'autre vue.
  const [hidden, setHidden] = useState<ServerError>(undefined);
  const serverError = state.error && state.error !== hidden ? state.error : undefined;

  const view = {
    auth,
    busy: state.status === 'busy',
    email,
    setEmail,
    serverError,
    dismissError: () => setHidden(state.error),
    focusTitle: switched,
  };
  const switchTo = (next: Mode) => () => {
    setHidden(state.error);
    setSwitched(true);
    setMode(next);
  };

  return (
    <main className="auth-page">
      {mode === 'login' ? <LoginView {...view} onSwitch={switchTo('register')} /> : <RegisterView {...view} onSwitch={switchTo('login')} />}
    </main>
  );
}

/** Recovery Key : montrée une seule fois, jamais renvoyée au serveur. */
export function RecoveryKeyDialog({ recoveryKey, onDone }: { recoveryKey: string; onDone: () => void }) {
  const [noted, setNoted] = useState(false);
  return (
    <div className="lh-scrim">
      <div className="lh-modal" role="dialog" aria-modal="true" aria-labelledby="rk-title" style={{ maxWidth: 520 }}>
        <h3 id="rk-title">Votre clé de récupération</h3>
        <p className="lh-modal-body">
          Elle permet de retrouver vos données si vous oubliez le mot de passe maître. Notez-la dans un endroit sûr : elle ne sera plus jamais affichée et LifeHub ne peut pas la régénérer pour vous.
        </p>
        <code style={{ display: 'block', padding: 14, borderRadius: 10, background: 'var(--sunken)', border: '1px solid var(--line)', font: '500 15px/24px var(--font-mono)', letterSpacing: '.04em', wordBreak: 'break-word', marginBottom: 18, userSelect: 'all' }}>{recoveryKey}</code>
        <label className="lh-check lh-check-box" style={{ marginBottom: 22 }}>
          <input type="checkbox" checked={noted} onChange={(e) => setNoted(e.target.checked)} />
          <span className="lh-box">{noted ? '✓' : null}</span>
          J’ai noté cette clé en lieu sûr
        </label>
        <div className="lh-modal-actions">
          <button type="button" className="lh-btn lh-btn-primary" disabled={!noted} onClick={onDone}>Continuer</button>
        </div>
      </div>
    </div>
  );
}
